import { useMemo } from "react";
import { type MediaItem, SortableMediaList } from "./SortableMediaList";

/**
 * One setlist row. `id` keys the row; `songId` is null for a song not kept in
 * Bandwith, which shows just its title, with no link or recording.
 */
type SongItem = {
  id: string;
  songId: string | null;
  title: string;
  author?: { name?: string | null } | null;
  /** Chosen take for this setlist; null/undefined follows the primary recording */
  recordingId?: string | null;
  /** All takes, for the per-setlist recording picker (edit mode) */
  recordings?: Array<{ id: string; filename: string; position: number }>;
  primaryRecording?: {
    id: string;
    filename: string;
    signedAudioUrl: string;
    durationSeconds: number;
  } | null;
};

type Props = {
  songs: SongItem[];
  setlistId?: string;
  isEditing: boolean;
  disabled?: boolean;
  onReorder?: (newOrder: string[]) => void;
  onRemove?: (songId: string) => void;
  onRecordingChange?: (songId: string, recordingId: string | null) => void;
};

export function SetlistSongsList({
  songs,
  setlistId,
  isEditing,
  disabled,
  onReorder,
  onRemove,
  onRecordingChange,
}: Props) {
  const songById = useMemo(
    () => new Map(songs.map((song) => [song.id, song])),
    [songs],
  );

  const items: MediaItem[] = useMemo(() => {
    return songs.map((song) => {
      const { songId } = song;
      if (!songId) return { id: song.id, title: song.title };
      const recording = song.primaryRecording ?? null;
      const linkHref = setlistId
        ? `/songs/${songId}?from=setlist-${setlistId}`
        : `/songs/${songId}`;
      return {
        id: song.id,
        title: song.title,
        subtitle: song.author?.name,
        linkHref,
        audio: recording
          ? {
              recordingId: recording.id,
              filename: recording.filename,
              signedUrl: recording.signedAudioUrl,
              songId,
              songTitle: song.title,
              authorName: song.author?.name ?? null,
              durationSeconds: recording.durationSeconds,
            }
          : null,
      };
    });
  }, [songs, setlistId]);

  const renderRecordingPicker = (item: MediaItem) => {
    const song = songById.get(item.id);
    const takes = song?.recordings ?? [];
    if (!onRecordingChange || takes.length < 2) return null;
    const primaryId = takes.find((r) => r.position === 0)?.id ?? takes[0]?.id;
    return (
      <select
        aria-label={`Recording for ${item.title}`}
        value={song?.recordingId ?? primaryId}
        disabled={disabled}
        onChange={(e) =>
          // Choosing the primary stores null: the setlist keeps following
          // whichever take is primary, rather than pinning the current one
          onRecordingChange(
            item.id,
            e.target.value === primaryId ? null : e.target.value,
          )
        }
        className="max-w-36 truncate rounded-md border border-border-subtle bg-bg-tertiary px-2 py-1 font-mono text-xs text-text-primary focus:border-amber focus:outline-none disabled:opacity-50"
      >
        {takes.map((take) => (
          <option key={take.id} value={take.id}>
            {take.filename}
          </option>
        ))}
      </select>
    );
  };

  return (
    <SortableMediaList
      items={items}
      isEditing={isEditing}
      disabled={disabled}
      onReorder={onReorder}
      onDelete={onRemove}
      renderEditControls={renderRecordingPicker}
      testIdPrefix="setlist-song"
    />
  );
}
